import { isFunction, isString, isUndefined, kebabCase } from 'lodash-es';
import { getContext } from 'svelte';
import {
  ACT_CSS_CLASSES,
  COMP_DEFAULT_BACKGROUND_IN_EDITOR,
  COMP_DEFAULT_FIXED_ZINDEX_IN_EDITOR,
  COMPONENT_PREFIX,
} from '../constants';
import { styleObjectToString, styleStringToObject, transformStyle } from '../utils';
import { useActVm } from './use-act-vm';

export function useProps(options: IUsePropsOptions) {
  const actVm = useActVm();
  const inEditor = getContext('inMagic');
  const {
    config,
    scrollHide,
    isContainer = false,
    attrs = {},
  } = options;
  const {
    html,
    display,
    act: buttonComponentHocType = '',
    className: configClassName,
    style: configStyle,
    disabledStyle: configDisabledStyle,
  } = config;

  let { type: componentType } = config;
  const { class: attrClass = '', style: attrStyle = '' } = attrs;

  let visible = true;
  const disabled = false;

  if (isFunction(display)) {
    // 这里还是包装组件的逻辑，组件还未初始化，
    // svelte 组件 mount 之前没有办法获取组件实例，因此这里bind到 null 了
    // 因此使用入参应该用 actVm 代替 curComponent 作为第一个参数
    visible = display.bind(null)(actVm, {});
  } else if (display === false) {
    visible = false;
  }

  let className = configClassName;

  const formatClassName = (value: any) => {
    if (isFunction(value)) {
      return value(actVm, {}) as Array<string>;
    }
    if (Array.isArray(value)) {
      return [...value] as Array<string>;
    }
    if (isString(value)) {
      return [value] as Array<string>;
    }
    return [];
  };

  const appendClassName = (value: Array<string>) => {
    if (disabled) {
      value.push(ACT_CSS_CLASSES.disabled);
    }
    if (!inEditor && scrollHide) {
      value.push(ACT_CSS_CLASSES.scrollHide);
    }
    return value;
  };

  const mergeClassName = (value: Array<string>) => {
    if (attrClass) {
      attrClass.split(' ').forEach((attrCls) => {
        if (!value.includes(attrCls)) {
          value.push(attrCls);
        }
      });
    }
    return value;
  };

  // 需要实现 svelte 的 watch/computed 功能，无法避免的写法
  // eslint-disable-next-line
  $: {
    if (!className) {
      className = attrClass;
    } else {
      className = formatClassName(className);
      className = appendClassName(className);
      className = mergeClassName(className);
      className = className.join(' ');
    }
  }

  let style = configStyle;

  const formatStyle = (value: Record<string, any>) => {
    if (disabled && configDisabledStyle) {
      let disabledStyle = getStyle(configDisabledStyle, attrStyle, actVm);

      // 过滤置灰场景下没有填写的样式，默认使用正常情况下的相同配置
      disabledStyle = Object.keys(disabledStyle).reduce((obj, key) => {
        if (disabledStyle[key]) {
          return {
            ...obj,
            [key]: disabledStyle[key],
          };
        }
        return { ...obj };
      }, {});

      return {
        ...value,
        ...disabledStyle,
      };
    }
    return value;
  };

  const setDefaultBackgroundColor = (st: Record<string, any>) => {
    const style = st;
    // 编辑器中容器没有背景色或背景图时，默认一个背景色，方便看到容器的存在
    if (inEditor
      && isContainer
      && !style.backgroundColor
      && !style.backgroundImage) {
      style.backgroundColor = COMP_DEFAULT_BACKGROUND_IN_EDITOR;
    }
    return style;
  };

  const setStyleDisplay = (st: Record<string, any>) => {
    const style = st;
    if (!visible) {
      style.display = 'none';
    }
    return style;
  };

  const styleDefaultPosition = (st: Record<string, any>) => {
    const style = st;
    if (style.position === 'fixed' && isUndefined(style.zIndex)) {
      style.zIndex = COMP_DEFAULT_FIXED_ZINDEX_IN_EDITOR;
    }
    return style;
  };

  // 需要实现 svelte 的 watch/computed 功能，无法避免的写法
  // eslint-disable-next-line
  $: {
    if (!style) {
      style = {};
    } else {
      style = getStyle(style, attrStyle, actVm);
      style = formatStyle(style);
      style = setDefaultBackgroundColor(style);
      style = setStyleDisplay(style);
      style = styleDefaultPosition(style);
    }
  }

  const show = (): void => {
    visible = true;
  };

  const hide = (): void => {
    visible = false;
  };

  componentType = `${COMPONENT_PREFIX}-${kebabCase(componentType)}`;

  // 按钮类型的高阶组件
  if (componentType === `${COMPONENT_PREFIX}-action-button`) {
    componentType = `${COMPONENT_PREFIX}-button`;
    const buttonHocType = kebabCase(buttonComponentHocType);

    // 链接类型
    if (buttonHocType === 'url') {
      componentType = `${COMPONENT_PREFIX}-link`;
      // 其他按钮
    } else if (buttonHocType && !buttonHocType.endsWith('pop')) {
      componentType = `${COMPONENT_PREFIX}-${buttonHocType}-button`;
    }
  }

  return {
    className,
    style: styleObjectToString(style),
    html,
    visible,
    show,
    hide,
    componentType,
    disabled,
  };
}

const getStyle = (
  originStyle: Function | Record<string, any> | string,
  attrStyle: string,
  actVm: Object,
) => {
  let style: Record<string, any>;

  if (isFunction(originStyle)) {
    // svelte 在组件mount之前没办法获取当前组件实例，因此这里 bind null
    style = originStyle.bind(null)(actVm, {}) as Record<string, any>;
  } else {
    style = transformStyle(originStyle);
  }

  const attrStyleObject = styleStringToObject(attrStyle);

  Object.keys(attrStyleObject).forEach((key) => {
    style[key] = attrStyleObject[key];
  });

  return style;
};

interface IUsePropsOptions {
  config: {
    type: string;
    html: string;
    display: boolean | ((vm: any, options: any) => boolean);
    act?: string;
    className?: string | ((vm: any, options: any) => string) | string[];
    style?: Record<string, any>;
    disabledStyle?: Record<string, any>;
  };
  scrollHide: boolean;
  isContainer?: boolean;
  attrs?: {
    class?: string;
    style?: string;
    [key: string]: any;
  };
}
